import React, { createContext, useContext } from 'react';
import { evidenceFresh } from '../../utils/screenActivity';
import { powerFieldLabels, resolvePowerObservations } from '../../utils/powerObservations';

export const AreaPowerContext = createContext({ enabled: false, byId: {}, pending: {} });

export function DevicePowerBadge({ deviceId, liveState, fallback }) {
  const context = useContext(AreaPowerContext);
  const power = context.enabled ? resolvePowerObservations(liveState, context.byId[deviceId]).summary : fallback;
  return <span className={`topology-live__signal topology-live__signal--${power.state}`}>{power.label}</span>;
}

const ageLabel = (sample) => {
  const time = Number(sample?.collectedAt);
  if (!Number.isFinite(time) || time <= 0) return 'Collection time unknown';
  const age = Date.now() - time;
  return `${age < -2000 ? 'Invalid future timestamp' : `${Math.max(0, Math.floor(age / 1000))}s ago`} · ${new Date(time).toLocaleTimeString()} · ${evidenceFresh(sample) ? 'Fresh' : 'Last observed'}`;
};
const fieldText = (field) => field?.status === 'observed' && field.value != null ? String(field.value)
  : `${field?.status || 'unknown'}${field?.reason ? ` (${field.reason})` : ''}`;

export default function DevicePowerDetails({ device, liveState }) {
  const context = useContext(AreaPowerContext);
  if (!context.enabled) return null;
  const entry = context.byId[device.id];
  const result = resolvePowerObservations(liveState, entry);
  const legacy = result.legacy;
  const nativeFields = result.native || {};
  const busy = context.pending[entry?.targetDeviceId];
  return <details className="device-power-details">
    <summary>Power details</summary>
    <p>Observations describe {device.name || 'this device'}. A player’s display state does not establish its TV’s state. Physical power is unknown when unreachable.</p>
    <p>Native app · device {entry?.nativeDeviceId || device.id} · {ageLabel(result.native || legacy)}</p>
    {legacy && <p>Native interactivity sample: {typeof legacy.displayInteractive === 'boolean' ? String(legacy.displayInteractive) : 'unknown'} · {ageLabel(legacy)}</p>}
    {result.adb && <p>ADB · target {result.adb.targetDeviceId} · relay {result.adb.relayDeviceId} · {ageLabel(result.adb)}</p>}
    {result.disagreements.length > 0 && <p className="device-power-details__conflict">Providers disagree: {result.disagreements.map((key) => powerFieldLabels[key]).join(', ')}. Both observations are retained below.</p>}
    <table><thead><tr><th>Observation</th><th>Native</th><th>ADB</th></tr></thead><tbody>
      {Object.entries(powerFieldLabels).map(([key, label]) => <tr key={key}><th>{label}</th>
        <td>{fieldText((result.fields[key]?.provider === 'native' ? result.fields[key] : null) || nativeFields[key] || (key === 'interactive' && typeof legacy?.displayInteractive === 'boolean'
          ? { status: 'observed', value: legacy.displayInteractive } : null))}</td>
        <td>{fieldText(result.adb?.fields?.[key])}</td></tr>)}
    </tbody></table>
    {(result.native?.displays?.length > 0 || result.adb?.displays?.length > 0) && <p>
      Displays: {[['Native', result.native], ['ADB', result.adb]].flatMap(([provider, sample]) => (sample?.displays || [])
        .map((display) => `${provider} ${display.id}: ${display.state || 'unknown'}`)).join(' · ')}
    </p>}
    {entry?.lastError && entry.lastRead && <details>
      <summary>Latest ADB read · {ageLabel(entry.lastRead)}</summary>
      <p>Target {entry.lastRead.targetDeviceId} · relay {entry.lastRead.relayDeviceId}. Last usable evidence remains above.</p>
      <dl>{Object.entries(powerFieldLabels).map(([key, label]) => <React.Fragment key={key}>
        <dt>{label}</dt><dd>{fieldText(entry.lastRead.fields?.[key])}</dd>
      </React.Fragment>)}</dl>
    </details>}
    <button type="button" disabled={!entry?.adbEligible || !entry.schemaAvailable || busy || entry.nextAttemptAt > Date.now()}
      onClick={() => context.refresh(device.id, true)}>{busy ? 'Reading power details…' : 'Refresh power details'}</button>
    <p>{entry?.schemaAvailable === false ? 'ADB details require the power-observation migration.'
      : !entry?.adbEligible ? 'No authorized ADB observation route is available.'
        : entry?.status === 'busy' ? 'ADB power read skipped: the target is busy.'
          : entry?.status === 'observations_unavailable' ? 'ADB power observation storage is unavailable.'
        : entry?.lastError ? `Last ADB read: ${entry.lastError}. A connected relay supporting power observations is required.` : context.error}</p>
    {entry?.nextAttemptAt > Date.now() && <small>Next eligible ADB read: {new Date(entry.nextAttemptAt).toLocaleTimeString()}. ADB summary evidence expires after 20 seconds; fallback reads are limited to one per 60 seconds, with failure backoff.</small>}
  </details>;
}
